import React, { useState, useEffect } from 'react';
import { Card, Form, Button, Row, Col, Alert } from 'react-bootstrap';
import { UserDemographics } from '../types';

interface UserDataFormProps {
  onSubmit: (data: UserDemographics) => void;
  initialData?: Partial<UserDemographics>;
  isLoading?: boolean;
  hideImage?: boolean;
}

const UserDataForm: React.FC<UserDataFormProps> = ({
  onSubmit,
  initialData,
  isLoading = false,
  hideImage = false,
}) => {
  const [formData, setFormData] = useState<Partial<UserDemographics>>({
    name: '',
    phone: '',
    dob: '',
    gender: undefined,
    height: undefined,
    weight: undefined,
    email: '',
    ...initialData,
  });

  const [errors, setErrors] = useState<Partial<Record<keyof UserDemographics, string>>>({});

  // Calculate age from DOB
  useEffect(() => {
    if (formData.dob) {
      const birthDate = new Date(formData.dob);
      const today = new Date();
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }
      
      setFormData(prev => ({ ...prev, age }));
    }
  }, [formData.dob]);

  const handleInputChange = (field: keyof UserDemographics, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof UserDemographics, string>> = {};

    if (!formData.name?.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.phone?.trim()) {
      newErrors.phone = 'Mobile number is required';
    } else {
      // Remove all non-digit characters for validation
      const phoneDigits = formData.phone.replace(/\D/g, '');
      if (phoneDigits.length !== 10) {
        newErrors.phone = 'Mobile number must be exactly 10 digits';
      }
    }

    if (!formData.dob) {
      newErrors.dob = 'Date of birth is required';
    }

    if (!formData.gender) {
      newErrors.gender = 'Gender is required';
    }

    if (!formData.height || formData.height <= 0) {
      newErrors.height = 'Height is required and must be greater than 0';
    }

    if (!formData.weight || formData.weight <= 0) {
      newErrors.weight = 'Weight is required and must be greater than 0';
    }

    if (!formData.email?.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validateForm()) {
      onSubmit(formData as UserDemographics);
    }
  };

  return (
    <Card className="mb-4">
      {!hideImage && (
        <div className="position-relative">
          <img
            src="/media/partner/aifacescan.jpeg"
            alt="AI Face Scan"
            className="w-100"
            style={{
              display: 'block',
              borderTopLeftRadius: '0.375rem',
              borderTopRightRadius: '0.375rem'
            }}
          />
        </div>
      )}
      <Card.Body>
        <Form onSubmit={handleSubmit}>
          <Row className="mb-3">
            <Col md={12}>
              <Form.Group>
                <Form.Label>
                  Full Name <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  value={formData.name || ''}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  placeholder="Enter your full name"
                  disabled={isLoading}
                  isInvalid={!!errors.name}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.name}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>
                  Mobile Number <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="tel"
                  value={formData.phone || ''}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  placeholder="1234567890"
                  disabled={isLoading}
                  isInvalid={!!errors.phone}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.phone}
                </Form.Control.Feedback>
                <Form.Text className="text-muted">
                  Enter 10-digit mobile number
                </Form.Text>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label>
                  Email <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="email"
                  value={formData.email || ''}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  placeholder="your.email@example.com"
                  disabled={isLoading}
                  isInvalid={!!errors.email}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.email}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>
                  Date of Birth <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="date"
                  value={formData.dob || ''}
                  onChange={(e) => handleInputChange('dob', e.target.value)}
                  max={new Date().toISOString().split('T')[0]}
                  disabled={isLoading}
                  isInvalid={!!errors.dob}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.dob}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <label htmlFor="gender" id="gender-label" className="form-label">
                  Gender <span className="text-danger">*</span>
                </label>
                <select
                  id="gender"
                  aria-labelledby="gender-label"
                  value={formData.gender || ''}
                  onChange={(e) => handleInputChange('gender', e.target.value as 'male' | 'female')}
                  disabled={isLoading}
                  className={`form-select ${errors.gender ? 'is-invalid' : ''}`}
                  title="Gender"
                >
                  <option value="">Select gender</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                </select>
                <div className="invalid-feedback">
                  {errors.gender}
                </div>
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>
                  Height (cm) <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="number"
                  value={formData.height || ''}
                  onChange={(e) => handleInputChange('height', parseFloat(e.target.value || '0'))}
                  placeholder="170"
                  min="50"
                  max="250"
                  disabled={isLoading}
                  isInvalid={!!errors.height}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.height}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label>
                  Weight (kg) <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="number"
                  value={formData.weight || ''}
                  onChange={(e) => handleInputChange('weight', parseFloat(e.target.value || '0'))}
                  placeholder="70"
                  min="20"
                  max="300"
                  disabled={isLoading}
                  isInvalid={!!errors.weight}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.weight}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          {formData.age && (
            <Row className="mb-3">
              <Col md={12}>
                <Alert variant="info">
                  <strong>Age:</strong> {formData.age} years
                </Alert>
              </Col>
            </Row>
          )}

          <Row>
            <Col md={12}>
              <Button
                variant="primary"
                type="submit"
                disabled={isLoading}
                className="w-100"
              >
                {isLoading ? 'Processing...' : 'Start Face Scan'}
              </Button>
            </Col>
          </Row>
        </Form>
      </Card.Body>
    </Card>
  );
};

export default UserDataForm;
